import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, MapPin, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { businessId, site, telephoneHref } from "@/lib/site";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  service: z.string().optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

const Contact = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [service, setService] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const raw = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      phone: formData.get("phone") as string,
      service,
      message: formData.get("message") as string,
    };

    const result = contactSchema.safeParse(raw);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    const { error } = await supabase.from("contact_submissions").insert({
      name: result.data.name,
      email: result.data.email,
      phone: result.data.phone || null,
      service: result.data.service || null,
      message: result.data.message,
    });

    setLoading(false);
    if (error) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly.", variant: "destructive" });
    } else {
      setSubmitted(true);
    }
  };

  return (
    <>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${site.siteUrl}/` },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${site.siteUrl}/contact` }
          ]
        })}
      </script>

      {/* Hero */}
      <section
        className="relative py-16 md:py-24 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: "url('/images/front.jpg')" }}
      >
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
        <div className="container relative z-10 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Tell us about your interior demolition project or request a detailed estimate.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground mb-4">Get in Touch</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Call, email, or send a message about your demolition scope. For photos and property details, use the estimate form.
                </p>
              </div>
              <div className="space-y-4">
                <a href={telephoneHref} className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                  <div className="p-2.5 rounded-lg bg-primary/10"><Phone className="w-5 h-5 text-primary" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="font-semibold">{site.phone}</p>
                  </div>
                </a>
                <a href={`mailto:${site.email}`} className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                  <div className="p-2.5 rounded-lg bg-primary/10"><Mail className="w-5 h-5 text-primary" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-semibold">{site.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/10"><MapPin className="w-5 h-5 text-primary" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Location</p>
                    <p className="font-semibold text-foreground">{site.city}, {site.region} {site.postalCode}</p>
              </div>
              <div className="mt-6 rounded-lg overflow-hidden border">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1226153.9484097473!2d-88.75138814955797!3d44.45467246774557!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8802fbd68d717511%3A0xc3ef492372070c2d!2sProfessional%20Property%20Cleanout%20LLC!5e0!3m2!1sen!2sus!4v1771752193452!5m2!1sen!2sus"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Professional Property Cleanout service area on Google Maps"
                />
              </div>
            </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <Card>
                <CardContent className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-secondary mx-auto mb-4" />
                      <h3 className="text-xl font-heading font-bold text-foreground mb-2">Thank You!</h3>
                      <p className="text-muted-foreground">We've received your message and will get back to you within 24 hours.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <h3 className="text-lg font-heading font-bold text-foreground">Send a Project Question</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input id="name" name="name" placeholder="Your full name" className="mt-1" />
                          {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input id="email" name="email" type="email" placeholder="you@example.com" className="mt-1" />
                          {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone">Phone</Label>
                          <Input id="phone" name="phone" type="tel" placeholder="(920) 555-1234" className="mt-1" />
                        </div>
                        <div>
                          <Label htmlFor="service">Service Needed</Label>
                          <Select value={service} onValueChange={setService}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="kitchen-demolition">Kitchen Demolition</SelectItem>
                              <SelectItem value="bathroom-demolition">Bathroom Demolition</SelectItem>
                              <SelectItem value="basement-demolition">Basement Demolition</SelectItem>
                              <SelectItem value="flooring-removal">Flooring Removal</SelectItem>
                              <SelectItem value="commercial-interior-demolition">Commercial Interior Demolition</SelectItem>
                              <SelectItem value="interior-demolition">Other Interior Demolition</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea id="message" name="message" placeholder="Tell us about your project..." rows={4} className="mt-1" />
                        {errors.message && <p className="text-destructive text-xs mt-1">{errors.message}</p>}
                      </div>
                      <Button type="submit" size="lg" className="w-full" disabled={loading}>
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
